import { FacebookCompleteRegistrationEvent, FacebookCustomEvent } from '../analytics/facebook';
import { FirebaseLoginEvent, FirebaseSignUpEvent } from '../analytics/firebase';
import config from '../config';
import routeConfiguration from '../routeConfiguration';
import { apiBaseUrl, backEndSignUp, checkForUserEmail, createUserWithIdp, getAuthConfig, proxyLogin } from '../util/api';
import { getStoredAuthEpoch, setStoredAuthEpoch } from '../util/authEpoch';
import hasEffectiveConsent from '../util/consent/effectiveConsent';
import { storableError } from '../util/errors';
import localStorageCookieStore from '../util/localStorageCookieStore';
import { logClearUserId, logError } from '../util/log';
import { isCordova, isIOS } from '../util/mobile/deviceDetection';
import { oneSignalLogin, oneSignalLogout } from '../util/mobile/oneSignal';
import { pathByRouteName } from '../util/routes';
import { startUpVanSite } from './startUp.duck';
//import { generateGeoLocationJson } from './marketplaceData.duck';
import { clearCurrentUser, fetchCurrentUser } from './user.duck';

const authenticated = (authInfo) => authInfo && authInfo.isAnonymous === false;

export const getDefaultRoutes = (from, intl) => {
  const routes = routeConfiguration();
  const baseUrl = apiBaseUrl();

  // Route where the user should be returned after authentication
  // This is used e.g. with EditListingPage and ListingPage
  const fromParam = from ? `from=${from}` : '';

  // Default route where user is returned after successfull authentication
  const defaultReturn = pathByRouteName('LandingPage', routes, {}, intl.locale);
  const defaultReturnParam = defaultReturn ? `&defaultReturn=${defaultReturn}` : '';

  // Route for confirming user data before creating a new user
  const defaultConfirm = pathByRouteName('ConfirmPage', routes, {}, intl.locale);
  const defaultConfirmParam = defaultConfirm ? `&defaultConfirm=${defaultConfirm}` : '';

  return { baseUrl, fromParam, defaultReturnParam, defaultConfirmParam };
};

// ================ Action types ================ //

export const AUTH_INFO_REQUEST = 'app/auth/AUTH_INFO_REQUEST';
export const AUTH_INFO_SUCCESS = 'app/auth/AUTH_INFO_SUCCESS';

export const SIGNIN_REQUEST = 'app/auth/SIGNIN_REQUEST';
export const SIGNIN_SUCCESS = 'app/auth/SIGNIN_SUCCESS';
export const SIGNIN_ERROR = 'app/auth/SIGNIN_ERROR';

export const LOGOUT_REQUEST = 'app/auth/LOGOUT_REQUEST';
export const LOGOUT_SUCCESS = 'app/auth/LOGOUT_SUCCESS';
export const LOGOUT_ERROR = 'app/auth/LOGOUT_ERROR';

export const SIGNUP_REQUEST = 'app/auth/SIGNUP_REQUEST';
export const SIGNUP_SUCCESS = 'app/auth/SIGNUP_SUCCESS';
export const SIGNUP_ERROR = 'app/auth/SIGNUP_ERROR';

export const CONFIRM_REQUEST = 'app/auth/CONFIRM_REQUEST';
export const CONFIRM_SUCCESS = 'app/auth/CONFIRM_SUCCESS';
export const CONFIRM_ERROR = 'app/auth/CONFIRM_ERROR';

export const CHECK_USER_EMAIL_REQUEST = 'app/auth/CHECK_USER_EMAIL_REQUEST';
export const CHECK_USER_EMAIL_SUCCESS = 'app/auth/CHECK_USER_EMAIL_SUCCESS';
export const CHECK_USER_EMAIL_ERROR = 'app/auth/CHECK_USER_EMAIL_ERROR';

// Generic user_logout action that can be handled elsewhere
// E.g. src/reducers.js clears store as a consequence
export const USER_LOGOUT = 'app/USER_LOGOUT';

// ================ Reducer ================ //

/** @type {import('../../types/vansite').VanSite.State.Auth} */
const initialState = {
  isAuthenticated: false,

  // scopes associated with current token
  authScopes: [],

  // auth info
  authInfoLoaded: false,

  // login
  signinError: null,
  signinInProgress: false,

  // logout
  logoutError: null,
  logoutInProgress: false,

  // signup
  signupError: null,
  signupInProgress: false,

  // confirm (create use with idp)
  confirmError: null,
  confirmInProgress: false,

  // check user email
  emailExists: false,
  checkUserEmailInProgress: false,
  checkUserEmailError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case AUTH_INFO_REQUEST:
      return state;
    case AUTH_INFO_SUCCESS:
      // authInfo() dispatches `authInfoSuccess(null)` when the SDK throws,
      // so payload may be null. Treat that as "no scopes" instead of crashing.
      return {
        ...state,
        authInfoLoaded: true,
        isAuthenticated: authenticated(payload),
        authScopes: payload?.scopes ?? [],
      };

    case SIGNIN_REQUEST:
      return {
        ...state,
        signinInProgress: true,
        signinError: null,
        logoutError: null,
        signupError: null,
      };
    case SIGNIN_SUCCESS:
      return { ...state, signinInProgress: false, isAuthenticated: true };
    case SIGNIN_ERROR:
      return { ...state, signinInProgress: false, signinError: payload };

    case LOGOUT_REQUEST:
      return { ...state, logoutInProgress: true, signinError: null, logoutError: null };
    case LOGOUT_SUCCESS:
      return { ...state, logoutInProgress: false, isAuthenticated: false, authScopes: [] };
    case LOGOUT_ERROR:
      return { ...state, logoutInProgress: false, logoutError: payload };

    case SIGNUP_REQUEST:
      return { ...state, signupInProgress: true, signinError: null, signupError: null };
    case SIGNUP_SUCCESS:
      return { ...state, signupInProgress: false };
    case SIGNUP_ERROR:
      return { ...state, signupInProgress: false, signupError: payload };

    case CONFIRM_REQUEST:
      return { ...state, confirmInProgress: true, signinError: null, confirmError: null };
    case CONFIRM_SUCCESS:
      return { ...state, confirmInProgress: false, isAuthenticated: true };
    case CONFIRM_ERROR:
      return { ...state, confirmInProgress: false, confirmError: payload };

    case CHECK_USER_EMAIL_REQUEST:
      return {
        ...state,
        emailExists: false,
        checkUserEmailInProgress: true,
        checkUserEmailError: null,
      };
    case CHECK_USER_EMAIL_SUCCESS:
      return { ...state, emailExists: payload, checkUserEmailInProgress: false };
    case CHECK_USER_EMAIL_ERROR:
      return { ...state, checkUserEmailInProgress: false, checkUserEmailError: payload };

    default:
      return state;
  }
}

// ================ Selectors ================ //

export const authenticationInProgress = (state) => {
  const { signinInProgress, logoutInProgress, signupInProgress } = state.auth;
  return signinInProgress || logoutInProgress || signupInProgress;
};

// ================ Action creators ================ //

export const authInfoRequest = () => ({ type: AUTH_INFO_REQUEST });
export const authInfoSuccess = (info) => ({ type: AUTH_INFO_SUCCESS, payload: info });

export const signinRequest = () => ({ type: SIGNIN_REQUEST });
export const signinSuccess = () => ({ type: SIGNIN_SUCCESS });
export const signinError = (error) => ({ type: SIGNIN_ERROR, payload: error, error: true });

export const logoutRequest = () => ({ type: LOGOUT_REQUEST });
export const logoutSuccess = () => ({ type: LOGOUT_SUCCESS });
export const logoutError = (error) => ({ type: LOGOUT_ERROR, payload: error, error: true });

export const signupRequest = () => ({ type: SIGNUP_REQUEST });
export const signupSuccess = () => ({ type: SIGNUP_SUCCESS });
export const signupError = (error) => ({ type: SIGNUP_ERROR, payload: error, error: true });

export const confirmRequest = () => ({ type: CONFIRM_REQUEST });
export const confirmSuccess = () => ({ type: CONFIRM_SUCCESS });
export const confirmError = (error) => ({ type: CONFIRM_ERROR, payload: error, error: true });

export const checkUserEmailRequest = () => ({ type: CHECK_USER_EMAIL_REQUEST });
export const checkUserEmailSuccess = (payload) => ({ type: CHECK_USER_EMAIL_SUCCESS, payload });
export const checkUserEmailError = (error) => ({
  type: CHECK_USER_EMAIL_ERROR,
  payload: error,
  error: true,
});

export const userLogout = () => ({ type: USER_LOGOUT });

// ================ Thunks ================ //

export const authInfo = () => async (dispatch, getState, sdk) => {
  dispatch(authInfoRequest());

  try {
    const info = await sdk.authInfo();
    return dispatch(authInfoSuccess(info));
  } catch (e) {
    // Requesting auth info just reads the token from the token
    // store (i.e. cookies), and should not fail in normal
    // circumstances. If it fails, it's due to a programming
    // error. In that case we mark the operation done and dispatch
    // `null` success action that marks the user as unauthenticated.
    logError(e, 'auth-info-failed');
    dispatch(authInfoSuccess(null));
  }
};

/**
 * Einmaliger Zwangs-Logout über die Auth-Epoch (ADR-002): Meldet der Server
 * (GET /api/auth/config) eine neuere Epoch als die lokal quittierte, wird eine
 * bestehende Session ausgeloggt (sdk.logout widerruft das Refresh-Token), damit
 * der nächste Login über den Proxy die Lazy Migration nach vansite-auth anstößt.
 * Fail open: erreicht der Config-Call den Server nicht oder scheitert der
 * Logout, passiert nichts und der Check läuft beim nächsten App-Start erneut.
 * Wird beim Boot fire-and-forget dispatcht (src/index.js) — nie render-blockierend.
 */
export const enforceAuthEpoch = () => async (dispatch, getState, sdk) => {
  try {
    const { authEpoch } = (await getAuthConfig()) || {};
    if (!Number.isInteger(authEpoch) || authEpoch <= 0) return; // Feature aus
    if (getStoredAuthEpoch() >= authEpoch) return; // bereits quittiert

    const info = await sdk.authInfo();
    if (authenticated(info)) {
      // logout() rejected nie — bei Fehlern dispatcht es logoutError und gibt
      // undefined zurück. Dann NICHT quittieren, nächster Boot versucht es neu.
      const response = await dispatch(logout());
      if (!response) return;
    }
    setStoredAuthEpoch(authEpoch);
  } catch (e) {
    logError(e, 'enforce-auth-epoch-failed');
  }
};

export const signin = (username, password) => async (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  dispatch(signinRequest());

  try {
    if (config.proxyLoginEnabled) {
      // marketplace-web@P1 (ADR-002): Sharetribe-Grant serverseitig; das
      // Antwort-Cookie ist SDK-kompatibel, danach nur den Token-Store einlesen.
      try {
        const proxyResponse = await proxyLogin({
          email: username,
          password,
          platform: isCordova() ? 'cordova' : 'web',
        });
        // Cordova: der Login-POST läuft über das native HTTP-Plugin — dessen
        // Set-Cookie erreicht weder WebView noch localStorage (den SDK-Store
        // auf Cordova). Der Server liefert den Token deshalb zusätzlich im
        // Body (Cordova-Token-Bridge, server/api/auth/vansite-auth.js).
        if (isCordova() && proxyResponse?.token) {
          localStorageCookieStore({ clientId: config.sdk.clientId }).setToken(proxyResponse.token);
        }
        // Session muss nach dem Proxy-Login wirklich stehen — kam der Token
        // nicht im Store an, ist der Proxy-Pfad kaputt: Fallback statt
        // Login-Loop mit irreführendem Offline-Banner.
        const info = await sdk.authInfo();
        if (!authenticated(info)) {
          throw new Error('proxy login did not establish a session');
        }
      } catch (e) {
        // 401 = Sharetribe hat die Zugangsdaten geprüft und abgelehnt — der
        // Fallback würde mit denselben Credentials identisch scheitern.
        if (e?.status === 401) {
          throw e;
        }
        // Jeder andere Proxy-Fehler (409 Server-Flag aus, 5xx, Timeout, 404, 429)
        // heißt "Proxy-Pfad gestört", nicht "Zugangsdaten falsch": sdk.login geht
        // Browser->flex-api direkt und funktioniert auch bei Backend-Ausfall.
        // 409 ist der erwartete Rollback-Pfad und kein Telemetrie-Fall.
        if (e?.status !== 409) {
          logError(e, 'proxy-login-fallback', { username });
        }
        await sdk.login({ username, password });
      }
    } else {
      await sdk.login({ username, password });
    }
    dispatch(signinSuccess());
    await dispatch(fetchCurrentUser());
    const { currentUser, currentUserHasListings } = getState().user;
    dispatch(startUpVanSite());
    oneSignalLogin(currentUser, currentUserHasListings);

    const loginMethod = `sharetribe_${isCordova() ? (isIOS() ? 'ios' : 'android') : 'web'}`;
    FirebaseLoginEvent.withLoginMethod(loginMethod).logEvent();
    FacebookCustomEvent.withCurrentUserUUID(currentUser?.id?.uuid)
      .withCustomName('Login')
      .withParams({
        login_method: `sharetribe_${isCordova() ? (isIOS() ? 'ios' : 'android') : 'web'}`,
      })
      .logEvent();
  } catch (e) {
    // 401 on signin means invalid credentials — an expected user flow, not a bug worth paging on.
    if (e?.status !== 401) {
      logError(e, 'signin-error', { username });
    }
    dispatch(signinError(storableError(e)));
  }
};

export const logout = () => async (dispatch, getState, sdk) => {
  if (authenticationInProgress(getState())) {
    return Promise.reject(new Error('Login or logout already in progress'));
  }
  // const userId = getState().user.currentUser.id.uuid;
  dispatch(logoutRequest());

  // Note that the thunk does not reject when the logout fails, it
  // just dispatches the logout error action.

  try {
    const response = await sdk.logout();
    dispatch(logoutSuccess());
    dispatch(clearCurrentUser());
    dispatch(userLogout());
    oneSignalLogout();
    logClearUserId();

    return response;
  } catch (e) {
    logError(e, 'logout-failed');
    dispatch(logoutError(storableError(e)));
  }
};
/**
 * @param {object} params - The parameters for the signup.
 * @param {string} params.email - The email of the user.
 * @param {string} params.password - The password of the user.
 * @param {string} params.firstName - The first name of the user.
 * @param {string} params.lastName - The last name of the user.
 * @param {object} params.phoneNumberVerification - The phone number verification of the user.
 * @param {boolean} params.phoneNumberVerification.approved - Whether the phone number verification is approved.
 * @param {boolean} params.approvedForNewsletter - Whether the user is approved for the newsletter.
 * @param {object} params.protectedData - The protected data of the user.
 * @param {object} params.privateData - The private data of the user.
 * @param {string | null} referralId - The referral ID of the user.
 * @param {{partnerName: string, partnerUserId: string} | null} partnerRef - Partner-Referenz (Haspa) aus localStorage.
 * @returns {import('../../types/vansite').AppThunkAction<Promise<void>>}
 */
export const signup =
  (params, referralId = null, partnerRef = null) =>
    async (dispatch, getState, sdk) => {
      if (authenticationInProgress(getState())) {
        return Promise.reject(new Error('Login or logout already in progress'));
      }
      dispatch(signupRequest());
      const { email, password, firstName, lastName, ...restParams } = params;
      const { phoneNumberVerification, approvedForNewsletter, ...rest } = restParams;

      const displayName = `${firstName} ${lastName.charAt(0).toUpperCase()}`;
      const createUserParams = Object.keys(rest).length === 0
        ? { email, password, firstName, lastName }
        : {
          email,
          password,
          firstName,
          lastName,
          displayName,
          protectedData: { ...rest },
          privateData: {
            mobileAppRegister: isCordova(),
            lastPolicyAcknowledged: new Date().toISOString(),
            language: navigator.language.split('-')[0],
            notificationLocale: navigator.language.split('-')[0],
            phoneNumberVerification,
            notificationSettings: {
              email: {
                systemNotifications: true,
                newMessages: true,
                newBooking: true,
                newsLetter: approvedForNewsletter,
              },
              push: {
                systemNotifications: true,
                newBooking: true,
                newMessages: true,
                news: true,
              },
              sms: {
                newBooking: true,
              },
            },
          },
        };
      try {
        const marketingConsent = await hasEffectiveConsent('marketing').catch((e) => {
          logError(e, 'signup-consent-check-failed');
          return false;
        });
        const backendResponse = await backEndSignUp({
          createUserParams,
          approvedForNewsletter: approvedForNewsletter,
          referralId,
          partnerRef,
          consent: { marketing: marketingConsent },
        });
        const newCustomer = backendResponse.data.data;
        dispatch(signupSuccess());

        const signupMethod = `sharetribe_${isCordova() ? (isIOS() ? 'ios' : 'android') : 'web'}`;
        FirebaseSignUpEvent.withSignUpMethod(signupMethod).logEvent();
        FacebookCompleteRegistrationEvent.withCurrentUserUUID(newCustomer?.id?.uuid)
          .withEventID(newCustomer.id.uuid)
          .logEvent();

        await dispatch(signin(email, password));
      } catch (e) {
        dispatch(signupError(storableError(e)));
        // No PII in Sentry: the failing request (status, apiErrorData) is
        // enough to diagnose; a specific user is looked up in Sharetribe.
        logError(e, 'signup-failed');
      }
    };

export const signupWithIdp = (params) => (dispatch) => {
  dispatch(confirmRequest());
  return createUserWithIdp(params)
    .then(() => {
      return dispatch(confirmSuccess());
    })
    .then(() => dispatch(fetchCurrentUser()))
    .catch((e) => {
      logError(e, 'create-user-with-idp-failed', { params });
      return dispatch(confirmError(storableError(e)));
    });
};

export const checkUserEmail = (email) => async (dispatch) => {
  dispatch(checkUserEmailRequest());
  try {
    const response = await checkForUserEmail({ email });
    const { exists } = response.data;
    dispatch(checkUserEmailSuccess(exists));
    return exists;
  } catch (e) {
    logError(e, 'check-user-email-failed', { email });
    dispatch(checkUserEmailError(storableError(e)));
    throw e; // throw error to stop signup process
  }
};
